import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './index';
import type { Timeframe } from '../types/Timeframe';
import type { StudyItem } from '../types/utils';

interface ChartConfig {
    instrument: string | null;
    timeframe: Timeframe;
    studies: StudyItem[];
}

export interface ChartState {
    activeSymbol: string | null;
    isChartOpen: boolean;
    inputs: Record<number, ChartConfig>;
    selectedChart: number;
    screenSplitIndex: number;
    cols: number;
    rows: number;
}

const initialState: ChartState = {
    activeSymbol: null,
    isChartOpen: false,
    inputs: {
        0: {
            instrument: null,
            timeframe: '1day',
            studies: []
        }
    },
    selectedChart: 0,
    screenSplitIndex: 0,
    cols: 1,
    rows: 1,
};

const chartSlice = createSlice({
    name: 'chart',
    initialState,
    reducers: {
        openChart: (state, action: PayloadAction<string>) => {
            state.activeSymbol = action.payload;
            state.isChartOpen = true;
            // Update the current selected chart's instrument
            state.inputs[state.selectedChart].instrument = action.payload;
        },
        closeChart: (state) => {
            state.activeSymbol = null;
            state.isChartOpen = false;
        },
        setChartTimeframe: (state, action: PayloadAction<{ chartIndex: number; timeframe: Timeframe }>) => {
            const { chartIndex, timeframe } = action.payload;
            if (state.inputs[chartIndex]) {
                state.inputs[chartIndex].timeframe = timeframe;
            }
        },
        setChartStudies: (state, action: PayloadAction<{ chartIndex: number; studies: StudyItem[] }>) => {
            const { chartIndex, studies } = action.payload;
            if (state.inputs[chartIndex]) {
                state.inputs[chartIndex].studies = studies;
            }
        },
        addChartStudy: (state, action: PayloadAction<{ chartIndex: number; study: StudyItem }>) => {
            const { chartIndex, study } = action.payload;
            if (state.inputs[chartIndex]) {
                const exists = state.inputs[chartIndex].studies.some(s => s.id === study.id);
                if (!exists) {
                    state.inputs[chartIndex].studies.push(study);
                }
            }
        },
        removeChartStudy: (state, action: PayloadAction<{ chartIndex: number; studyId: string }>) => {
            const { chartIndex, studyId } = action.payload;
            if (state.inputs[chartIndex]) {
                state.inputs[chartIndex].studies = state.inputs[chartIndex].studies.filter(s => s.id !== studyId);
            }
        },
        setSelectedChart: (state, action: PayloadAction<number>) => {
            state.selectedChart = action.payload;
        },
        setScreenLayout: (state, action: PayloadAction<{ index: number; cols: number; rows: number }>) => {
            const { index, cols, rows } = action.payload;
            state.screenSplitIndex = index;
            state.cols = cols;
            state.rows = rows;
        },
        duplicateChartToIndex: (state, action: PayloadAction<{ sourceIndex: number; targetIndex: number }>) => {
            const { sourceIndex, targetIndex } = action.payload;
            if (state.inputs[sourceIndex]) {
                state.inputs[targetIndex] = { ...state.inputs[sourceIndex] };
            }
        },
        resetChartState: () => initialState,
    },
});

export const {
    openChart,
    closeChart,
    setChartTimeframe,
    setChartStudies,
    addChartStudy,
    removeChartStudy,
    setSelectedChart,
    setScreenLayout,
    duplicateChartToIndex,
    resetChartState,
} = chartSlice.actions;

// Selectors
export const selectActiveSymbol = (state: RootState) => state.chart.activeSymbol;
export const selectIsChartOpen = (state: RootState) => state.chart.isChartOpen;
export const selectChartInputs = (state: RootState) => state.chart.inputs;
export const selectSelectedChart = (state: RootState) => state.chart.selectedChart;
export const selectScreenSplitIndex = (state: RootState) => state.chart.screenSplitIndex;
export const selectChartLayout = (state: RootState) => ({
    cols: state.chart.cols,
    rows: state.chart.rows,
    screenSplitIndex: state.chart.screenSplitIndex,
});
export const selectChartConfig = (chartIndex: number) => (state: RootState) =>
    state.chart.inputs[chartIndex];

export default chartSlice.reducer;
